/**
 * ActionConfigBuilderDemo.jsx
 * ===========================
 * Demo page for Action Configuration System
 * Shows configuration form and saved actions list
 * 
 * Created: 2025-11-20
 */

import React, { useState } from 'react';
import ActionConfigBuilder from './ActionConfigBuilder';
import ActionsList from '../components/actions/ActionsList';
import { Plus, List } from 'lucide-react';

const ActionConfigBuilderDemo = () => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'config'
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editAction, setEditAction] = useState(null);

  const handleEdit = (action) => {
    setEditAction(action);
    setViewMode('config');
  };

  const handleActionSaved = () => {
    // Trigger refresh of actions list
    setRefreshTrigger(prev => prev + 1);
    setViewMode('list');
    setEditAction(null);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Action Configuration</h1>
          <p style={styles.subtitle}>
            Create and manage reusable actions for your application
          </p>
        </div>
        <div style={styles.headerButtons}>
          <button
            onClick={() => {
              setViewMode('list');
              setEditAction(null);
            }}
            style={{
              ...styles.modeButton,
              ...(viewMode === 'list' ? styles.modeButtonActive : {})
            }}
          >
            <List size={18} />
            View Actions
          </button>
          <button
            onClick={() => {
              setViewMode('config');
              setEditAction(null);
            }}
            style={{
              ...styles.modeButton,
              ...(viewMode === 'config' ? styles.modeButtonActive : {})
            }}
          >
            <Plus size={18} />
            Create New
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {viewMode === 'list' ? (
          <>
            <div style={styles.listHeader}>
              <h2 style={styles.listTitle}>Saved Actions</h2>
              <button
                onClick={() => setViewMode('config')}
                style={styles.createButton}
              >
                <Plus size={18} />
                Create Action
              </button>
            </div>
            <ActionsList
              onEdit={handleEdit}
              refreshTrigger={refreshTrigger}
            />
          </>
        ) : (
          <ActionConfigBuilder
            isPopup={false}
            onActionSelect={null}
            onClose={null}
          />
        )}
      </div>
    </div>
  );
};

// Inline styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
    padding: '24px',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '14px',
    color: '#6B7280',
  },
  headerButtons: {
    display: 'flex',
    gap: '8px',
  },
  modeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#FFFFFF',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  modeButtonActive: {
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    borderColor: '#3B82F6',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  listTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default ActionConfigBuilderDemo;

